import React from 'react';
import { onDisplayAdvertisingMode } from 'src/store/modules/user/selectors';
import BeaconMetricColumnChart from 'src/components/BeaconV2Components/BeaconMetricColumnChart';
import removeOtherTraffic from 'src/components/BeaconV2Components/utils/removeOtherTraffic';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const metricToTitle = {
  totalClicks: 'Total Traffic',
  organicClicks: 'Organic Traffic',
  adClicks: 'Paid Traffic'
};

const groupByFieldToTitle = {
  categoryId: 'Category',
  searchTerm: 'Keyword',
  subCategoryId: 'Subcategory'
};

/**
 * Shows a bar chart that displays traffic by a specific metric and groupByField,
 * for usage in the total, organic, and paid search traffic pages.
 */
export function TrafficAdvancedSearchBarChart({
  metric,
  groupByField
}: {
  metric: 'totalClicks' | 'organicClicks' | 'adClicks';
  groupByField: 'categoryId' | 'searchTerm' | 'subCategoryId';
}) {
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodShortDisplayName = useComparisonTimePeriodShortDisplayName();
  const isAdvertisingDisplayMode = useAppSelector(onDisplayAdvertisingMode);

  return (
    <BeaconMetricColumnChart
      fieldName={metric}
      indexName={groupByField === 'searchTerm' || !isAdvertisingDisplayMode ? 'traffic' : 'aggregated-traffic'}
      useAdvancedSearchDataArgs={{
        fields: [{ name: metric }],
        groupByField,
        requestBuilder: (builder) => {
          const build = builder.apply(removeOtherTraffic);
          if (!isAdvertisingDisplayMode) {
            build.addConditionTermFilter('searchTerm', 'must_not', ['[DSP Traffic]']).modifyAggregation((agg) => {
              return agg.addConditionTermFilter('searchTerm', ['[DSP Traffic]'], 'must_not');
            });
          }
          return build;
        }
      }}
      beaconChartWithLegendPropsBuilder={(props) => ({
        ...props,
        title: `${metricToTitle[metric]} by ${groupByFieldToTitle[groupByField]}`,
        includeSummaryInfo: false,
        subtitle: (
          <BeaconSubtitleContainer>
            <Text variant="body1">
              {`The chart displays the ${metricToTitle[metric].toLowerCase()} for `}
              {entityPhrase} {taxonomyText}
              {` in each ${groupByFieldToTitle[groupByField].toLocaleLowerCase()}`} during the{' '}
              {mainTimePeriodShortDisplayName} period compared to the {comparisonTimePeriodShortDisplayName}.
              <SubtitleFilters />
            </Text>
          </BeaconSubtitleContainer>
        )
      })}
    />
  );
}
